import { memo, useCallback } from 'react';

import filesize from 'filesize';

import { FileIcon, defaultStyles } from 'react-file-icon';

import { ProgressBar } from './progressBar/progressBar.fileUpload';

import { FileType } from '../types';
import { Check, X } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { Button } from '@/shadcn/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import { getFileExtension } from '@/components/fileIcon/file.utils';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';

interface Props {
  file: FileType;
  maxFileSize: number;
  handleDeleteFile: (x: any) => void;
  disabled: boolean;
  fieldName: string;
}

export const File = memo(
  ({ file, handleDeleteFile, maxFileSize, disabled, fieldName }: Props) => {
    const { id, name, size, uploadPercent, errorType } = file;

    const isUploading = uploadPercent! >= 0;
    const fileExtension = getFileExtension(name);

    const renderSize = useCallback(() => {
      if (isUploading) {
        return <ProgressBar completed={uploadPercent} />;
      }
      return (
        <div
          className="text-muted-foreground flex items-center gap-2 text-[10px] uppercase"
          title={`File size: ${filesize(size)}`}
        >
          <span>{`${fileExtension}  ·  ${filesize(size, { round: 1 })}`}</span>
          <Check size={16} className="text-valid" />
        </div>
      );
    }, [isUploading, uploadPercent, size]);

    const renderError = useCallback(() => {
      switch (errorType) {
        case 'size':
          return (
            <div
              data-cy={`file-${fieldName}-error-size`}
              className="text-destructive"
              data-testid="file-error"
            >
              File size error: this file is over the {filesize(maxFileSize)}{' '}
              limit.
            </div>
          );
        case 'extension':
          return (
            <div
              data-cy={`file-${fieldName}-error-extension`}
              className="text-destructive"
              data-testid="file-error"
            >
              File type error:{' '}
              {`.${fileExtension} files are not supported in
                Trace.`}
            </div>
          );
        default:
          return (
            <div
              data-cy={`file-${fieldName}-error-default`}
              className="text-destructive"
              data-testid="file-error"
            >
              File error.
            </div>
          );
      }
    }, [errorType, maxFileSize, fileExtension]);

    return (
      <div
        className={cn(
          'text-foreground border-border hover:bg-muted flex w-full items-center justify-between gap-5 rounded-md border px-4 py-2 transition-colors',
          !!errorType && 'text-destructive border-destructive'
        )}
        key={id}
        data-cy="forms-js-file"
      >
        <div className="flex size-6 items-center justify-center rounded-sm">
          <FileIcon
            extension={fileExtension}
            {...defaultStyles[fileExtension]}
          />
        </div>

        <div className="flex grow flex-col flex-nowrap gap-1 overflow-hidden">
          <TextEllipsis
            className="text-foreground grow text-xs font-semibold overflow-ellipsis"
            text={name}
            middleEllipsis
          />

          <div className="text-muted-foreground grow text-[10px]">
            {!errorType ? renderSize() : renderError()}
          </div>
        </div>
        {!disabled && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="delete-file-button"
                  data-cy={`file-${fieldName}-${name}-delete-button`}
                  type="button"
                  variant="ghost"
                  aria-label="Delete file"
                  size="icon"
                  onClick={() => handleDeleteFile(id)}
                >
                  <X size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Delete <b>{name}</b>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }
);
